<script setup lang="ts">
import { ref } from 'vue';
import CourseCardRow, { type CourseCredit } from './CourseCardRow.vue';
import { AddToCalendarMenu, EmailInviteModal } from '@/components/common';
import { useUserStore } from '@/stores';

export interface EnrolledCourse {
  id: string | number;
  title: string;
  dateTime: string;
  duration: string;
  /** Credential codes the row should render as chips (e.g. `['cle']`, `['cle', 'cpe']`). */
  credentialCodes: string[];
  credits?: CourseCredit[];
  launchUrl?: string | null;
  /** CourseLiveEvent.Id — present when the row should render the Add to Calendar menu. */
  liveEventId?: number;
  /** ISO UTC timestamp of the live event start — required alongside liveEventId. */
  scheduledAtUtc?: string;
  /** Raw duration in minutes — required alongside liveEventId. */
  durationMinutes?: number;
  description?: string;
}

const props = withDefaults(
  defineProps<{
    courses: EnrolledCourse[];
    courseLinkPrefix?: string;
  }>(),
  {
    courseLinkPrefix: '/course',
  }
);

const emit = defineEmits<{
  viewJurisdictions: [learningObjectId: string | number, credits: CourseCredit[]];
}>();

const userStore = useUserStore();
const inviteTarget = ref<EnrolledCourse | null>(null);

function handleViewJurisdictions(learningObjectId: string | number, credits: CourseCredit[]) {
  emit('viewJurisdictions', learningObjectId, credits);
}

function openInvite(course: EnrolledCourse) {
  inviteTarget.value = course;
}

function closeInvite() {
  inviteTarget.value = null;
}

function courseUrl(course: EnrolledCourse): string {
  return `${window.location.origin}${props.courseLinkPrefix}/${course.id}?deliveryMode=Live`;
}
</script>

<template>
  <div v-if="courses.length > 0" class="dashboard-section dashboard-section-full">
    <div class="section-header">
      <h2>Enrolled</h2>
    </div>
    <div class="course-cards-list">
      <CourseCardRow
        v-for="course in courses"
        :key="course.id"
        :id="course.id"
        :title="course.title"
        :date-time="course.dateTime"
        :duration="course.duration"
        :credential-codes="course.credentialCodes"
        :credits="course.credits"
        delivery-mode="Live"
        action-label="Join"
        :action-url="course.launchUrl ?? `${courseLinkPrefix}/${course.id}?deliveryMode=Live`"
        compact
        @view-jurisdictions="handleViewJurisdictions"
      >
        <template
          v-if="course.liveEventId && course.scheduledAtUtc && course.durationMinutes"
          #date-action
        >
          <AddToCalendarMenu
            :live-event-id="course.liveEventId"
            :event-title="course.title"
            :scheduled-at-utc="course.scheduledAtUtc"
            :duration-minutes="course.durationMinutes"
            :course-url="courseUrl(course)"
            :course-description="course.description"
            @open-email-invite="openInvite(course)"
          >
            <template #trigger="{ toggle, isOpen, menuId }">
              <button
                type="button"
                class="date-chevron-btn"
                :aria-expanded="isOpen"
                aria-haspopup="true"
                :aria-controls="menuId"
                aria-label="Add to calendar"
                @click="toggle"
              >
                <bb-icon :name="isOpen ? 'expand_less' : 'expand_more'" decorative></bb-icon>
              </button>
            </template>
          </AddToCalendarMenu>
        </template>
      </CourseCardRow>
    </div>

    <EmailInviteModal
      v-if="inviteTarget && inviteTarget.liveEventId && inviteTarget.scheduledAtUtc"
      :is-open="true"
      :live-event-id="inviteTarget.liveEventId"
      :event-title="inviteTarget.title"
      :scheduled-at-utc="inviteTarget.scheduledAtUtc"
      :course-url="courseUrl(inviteTarget)"
      :default-email="userStore.profile?.email ?? ''"
      @close="closeInvite"
    />
  </div>
</template>

<style scoped>
.date-chevron-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 2px;
  margin-left: 2px;
  border-radius: 4px;
  cursor: pointer;
  color: var(--color-text-muted);
}

.date-chevron-btn:hover {
  background: var(--color-surface-hover);
  color: var(--color-text-primary);
}

.date-chevron-btn:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.date-chevron-btn bb-icon {
  font-size: 18px;
}
</style>
